import { ViewDetailsEyeIcon } from "components/icons";
import QuestionHeader from "components/feedback/QuestionHeader";
import TextFeedback from "components/feedback/TextFeedback";
import UserProfileCard from "components/feedback/UserProfileCard";
import { QuestionType } from "enums/form";
import { Feedback } from "types/feedback";
import { UserUtil } from "utils";
import Markdown from "react-markdown";

type FeedbackCardProps = {
  feedback: Feedback;
  formName: string;
  onViewDetails: () => void;
  showReviewer?: boolean;
  freeTextOnly?: boolean;
};

const FeedbackCard = ({
  feedback,
  formName,
  onViewDetails,
  showReviewer = true,
  freeTextOnly = false,
}: FeedbackCardProps) => {
  const { reviewer, answers } = feedback;
  const sortedAnswers = [...answers]
    .filter(
      (answer) =>
        !freeTextOnly ||
        answer.question.questionType !== QuestionType.MULTIPLE_CHOICE,
    )
    .sort((a, b) => a.question.sequence - b.question.sequence);

  return (
    <article
      className="group relative flex h-full cursor-pointer flex-col rounded-2xl bg-blue-10 p-6"
      onClick={onViewDetails}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onViewDetails();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <p className="body-16-semibold mb-4 text-right">{formName}</p>

      {showReviewer && (
        <div className="mb-4 min-w-0 space-y-1">
          <p className="body-12-regular text-neutral-50">Reviewer:</p>
          <UserProfileCard
            userProfile={UserUtil.getReviewerInfoToDisplay(reviewer)}
          />
        </div>
      )}

      <div className="space-y-5">
        {sortedAnswers.map((answer) => {
          const { question } = answer;

          return (
            <div key={answer.id}>
              <QuestionHeader
                sequence={question.sequence}
                label={question.label}
                hint={question.hint}
              />
              {question.questionType === QuestionType.TEXT ? (
                <TextFeedback
                  answer={answer}
                  className="body-14-regular break-words text-neutral-200 line-clamp-3 [&_p]:inline"
                />
              ) : (
                <div className="body-14-regular break-words text-neutral-200 line-clamp-3 [&_p]:inline">
                  <Markdown>{answer.answerOptions?.label ?? "—"}</Markdown>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-2xl bg-neutral-100/80 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden="true"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-0">
          <ViewDetailsEyeIcon className="h-6 w-6" />
        </div>
        <p className="body-14-medium text-neutral-0">View details</p>
      </div>
    </article>
  );
};

export default FeedbackCard;
